import {downloadFile} from '@domain/file-download'
import {useShiftSummary, useShiftSummaryDocument, useRequiredConfig} from '@domain/shifthandover'
import {DateRange} from '@hconnect/common'
import {FlexPage} from '@hconnect/common/components/FlexPage'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {FilterContrastProvider} from '@hconnect/common/styles'
import {queryRangeToTimeRange} from '@hconnect/common/utils'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Print as PrintIcon} from '@mui/icons-material'
import {Button, Grid} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {getFilterDifference, Page, FilterOptions} from '@widgets/shift-event-filters'
import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {ShiftSummaryFilterBar} from '../components'
import {FEATURE_NAMESPACE, SHIFT_SUMMARY_TITLE_ID} from '../consts'
import {ShiftSummaryView} from '../containers'
import {
  useFilterDatePicker,
  useShiftSummaryPageFilters,
  useTrackShiftSummaryAnalytics
} from '../hooks'
import {getButtonOutlineWhite} from '../styles'

type Props = {
  plantId: string
  dashboardUrl: string
  onEquipmentClick: (equipmentId: string, dateRange: DateRange) => void
  /**
   * Set to `true` when rendered on the legacy blue surface (Cockpit) so the
   * header controls use the on-blue contrast colours. Suite renders on the
   * light/dark page surface and keeps the default (`false`).
   */
  inverted?: boolean
}

export const ShiftSummaryPage: React.FC<Props> = ({
  plantId,
  dashboardUrl,
  onEquipmentClick,
  inverted = false
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const breakpoint = useWidth()
  const {country, shifts} = useRequiredConfig(plantId)
  const trackAnalyticsEvent = useTrackShiftSummaryAnalytics({plantId, country})
  const [filterOptions, setFilters] = useShiftSummaryPageFilters(plantId)
  const {timeRange, getDateRange, isSingleShift} = useFilterDatePicker(plantId, shifts)
  const [isMobileFilterOpen, openMobileFilter] = useState(false)
  const [isPreparingSummary, setIsPreparingSummary] = useState(false)
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const effectiveFilterOptions = {
    ...filterOptions,
    timeRange: filterOptions.timeRange ?? getDateRange()
  }

  const isProductionVolumesAllowed = useCommonFeatureFlag(FeatureFlagKey.ProductionVolumes, {
    plantId
  })
  const isExpertSystemAllowed = useCommonFeatureFlag(FeatureFlagKey.ExpertSystem, {
    plantId
  })
  const isSiloFillAllowed = useCommonFeatureFlag(FeatureFlagKey.SiloFillLevels, {
    plantId
  })
  const isSmall = breakpoint === 'xs' || breakpoint === 'sm'

  const summaryTimeRange = queryRangeToTimeRange(
    effectiveFilterOptions.timeRange,
    shifts,
    getPlantNow()
  )

  const {
    data: summaryPageData,
    isLoading: summaryDataLoading,
    isFetching: summaryDataFetching
  } = useShiftSummary({
    plantId,
    timezone,
    timeRange: summaryTimeRange,
    options: {
      enabled: isProductionVolumesAllowed
    }
  })

  const getSummaryPdf = useShiftSummaryDocument({plantId})

  useEffect(() => {
    if (getSummaryPdf.isSuccess && getSummaryPdf.data) {
      downloadFile(getSummaryPdf.data, `${t('summary.label.shiftSummaryReportFileName')}.pdf`)
      trackAnalyticsEvent('userDownloadsShiftSummaryPDF', {})
    }
  }, [getSummaryPdf.isSuccess, getSummaryPdf.data, t, trackAnalyticsEvent])

  useEffect(() => {
    if (getSummaryPdf.isSuccess || getSummaryPdf.isError) {
      setIsPreparingSummary(false)
    }
  }, [getSummaryPdf.isSuccess, getSummaryPdf.isError])

  const handleGetSummaryPdf = () => {
    setIsPreparingSummary(true)
    getSummaryPdf.mutate({
      timeRange,
      reportLanguage: language,
      includeProductionVolumes: isSingleShift
    })
  }

  const queryClient = useQueryClient()
  useCancelInactiveQueries(queryClient)

  return (
    <FlexPage
      appName="Cockpit"
      title={isMobileFilterOpen ? t('shiftEvent.action.filter') : t('summary.pageName')}
      titleId={SHIFT_SUMMARY_TITLE_ID}
      sxActionContent={isMobileFilterOpen ? {width: '100%'} : undefined}
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Grid container alignItems="center">
            <Grid item xs={isMobileFilterOpen ? 12 : undefined}>
              <ShiftSummaryFilterBar
                plantId={plantId}
                activeSettings={filterOptions}
                isCollapsible={isSmall}
                onChange={(nextFilterOptions: FilterOptions) => {
                  setFilters(nextFilterOptions)
                  trackAnalyticsEvent('userAdjustsFilter', {
                    filterPage: Page.Summary,
                    filterName: getFilterDifference(nextFilterOptions, filterOptions)
                  })
                }}
                openMobileFilter={openMobileFilter}
                isMobileFilterOpen={isMobileFilterOpen}
              />
            </Grid>
            {!isMobileFilterOpen && (
              <Grid item>
                <Button
                  data-test-id="summary-print-button"
                  onClick={handleGetSummaryPdf}
                  sx={(theme) => ({
                    margin: '8px 10px',
                    ...getButtonOutlineWhite(theme),
                    '@media print': {
                      display: 'none'
                    },
                    '&.MuiButton-root:first-of-type': {
                      margin: '8px 10px'
                    }
                  })}
                  disabled={isPreparingSummary}
                  variant="text"
                >
                  <PrintIcon />
                </Button>
              </Grid>
            )}
          </Grid>
        </FilterContrastProvider>
      }
    >
      <ShiftSummaryView
        plantId={plantId}
        isMobileFilterOpen={isMobileFilterOpen}
        isLoading={summaryDataLoading}
        isFetching={summaryDataFetching}
        summaryPageData={summaryPageData}
        isProductionVolumesAllowed={isProductionVolumesAllowed ?? false}
        isExpertSystemAllowed={isExpertSystemAllowed ?? false}
        isSiloFillAllowed={isSiloFillAllowed ?? false}
        filterOptions={effectiveFilterOptions}
        dashboardUrl={dashboardUrl}
        onEquipmentClick={onEquipmentClick}
      />
    </FlexPage>
  )
}
